import React from 'react';
import { Link } from 'react-router-dom';
import { X, Package, Loader2, Layers, ExternalLink, ShoppingCart } from 'lucide-react';
import { useLang } from '../../contexts/LanguageContext';
import { getLocalizedField } from '../../utils/localizedContent';

// URLs de búsqueda Cardmarket por tipo de TCG
const CARDMARKET_URLS = {
  pokemon:   'https://www.cardmarket.com/en/Pokemon/Products/Search?searchString=',
  magic:     'https://www.cardmarket.com/en/Magic/Products/Search?searchString=',
  yugioh:    'https://www.cardmarket.com/en/YuGiOh/Products/Search?searchString=',
  onepiece:  'https://www.cardmarket.com/en/OnePiece/Products/Search?searchString=',
  // Riftbound no está en Cardmarket — enlace a búsqueda comunitaria RiftScribe
  riftbound: 'https://riftscribe.gg/cards?search=',
};

export default function CardDetailModal({
  card,
  booster,
  products,
  loading,
  onClose,
  lang
}) {
  const { t } = useLang();
  if (!card) return null;

  const getCardmarketUrl = () => {
    const baseUrl = CARDMARKET_URLS[card.tcg_type] || CARDMARKET_URLS.pokemon;
    return `${baseUrl}${encodeURIComponent(card.name)}`;
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" 
      onClick={onClose}
    >
      <div 
        className="bg-[#12121A] border border-white/10 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#12121A] border-b border-white/10 p-4 flex items-center justify-between">
          <h2 className="text-white font-bold text-lg">{card.name}</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X size={20} className="text-[#A1A1AA]" />
          </button>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Card Image */}
            <div className="flex flex-col items-center">
              <img 
                src={card.image_large || card.image_url}
                alt={card.name}
                className="max-h-[400px] rounded-xl shadow-2xl"
              />
              
              {/* Price Link - Cardmarket for most, RiftScribe for Riftbound */}
              <a
                href={getCardmarketUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 w-full max-w-[280px] flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#FF9800]/20 to-[#FF5722]/20 border border-[#FF9800]/30 rounded-xl text-[#FF9800] font-semibold hover:from-[#FF9800]/30 hover:to-[#FF5722]/30 transition-all"
                data-testid="cardmarket-link"
              >
                <ShoppingCart size={18} />
                {card.tcg_type === 'riftbound'
                  ? t('catalog.view_on_riftscribe')
                  : t('catalog.cardmarket_prices')}
                <ExternalLink size={14} />
              </a>
            </div>
            
            {/* Card Info & Products */}
            <div>
              <div className="mb-6">
                <h3 className="text-white font-bold text-xl mb-2">{card.name}</h3>
                {card.set_name && (
                  <p className="text-[#A1A1AA] text-sm mb-1">
                    <span className="text-[#10B981]">{t('catalog.set_label')}</span> {card.set_name}
                  </p>
                )}
                {card.rarity && (
                  <p className="text-[#C9A962] text-sm mb-1">
                    <span className="text-[#A1A1AA]">{t('catalog.rarity_label')}</span> {card.rarity}
                  </p>
                )}
                {card.type_line && (
                  <p className="text-[#A1A1AA] text-sm">
                    {card.type_line}
                  </p>
                )}
              </div>
              
              {/* Products Section */}
              <div>
                <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                  <Package size={18} className="text-[#10B981]" />
                  {t('catalog.where_to_get_card')}
                </h4>
                
                {loading ? (
                  <div className="flex items-center gap-2 text-[#A1A1AA]">
                    <Loader2 size={16} className="animate-spin" />
                    {t('catalog.finding_products')}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Booster/Expansion Info */}
                    {booster ? (
                      <div className="bg-[#0A0A0E] border border-[#C9A962]/30 rounded-xl p-4">
                        <div className="flex items-center gap-3 mb-3">
                          {booster.logo_url && (
                            <img src={booster.logo_url} alt="" className="w-10 h-10 object-contain" />
                          )}
                          <div className="flex-1">
                            <p className="text-[#A1A1AA] text-xs">{t('catalog.expansion')}</p>
                            <p className="text-white font-semibold flex items-center gap-2">
                              <Layers size={14} className="text-[#C9A962]" />
                              {booster.name}
                            </p>
                            <p className="text-[#A1A1AA] text-xs">{booster.set_code} · {booster.total_cards} {t('catalog.cards')}</p>
                          </div>
                          <Link 
                            to={`/boosters/${booster.booster_id}`}
                            className="text-[#C9A962] text-xs hover:underline flex items-center gap-1"
                          >
                            {t('catalog.view_set')} <ExternalLink size={12} />
                          </Link>
                        </div>
                        
                        {/* Products linked to this booster */}
                        {products.length > 0 ? (
                          <div className="space-y-2 mt-3 pt-3 border-t border-white/10">
                            <p className="text-[#A1A1AA] text-xs mb-2">
                              {t('catalog.available_in_store')}
                            </p>
                            {products.map(product => (
                              <Link 
                                key={product.product_id}
                                to={`/store/${product.product_id}`}
                                className="flex items-center gap-3 bg-[#12121A] border border-white/5 rounded-lg p-3 hover:border-[#10B981]/50 transition-colors"
                              >
                                {product.images?.[0] && (
                                  <img src={product.images[0]} alt={product.name} className="w-12 h-12 rounded-lg object-cover" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-white text-sm font-semibold truncate">
                                    {getLocalizedField(product, 'name', lang, product.name)}
                                  </p>
                                  <p className="text-[#10B981] text-sm font-bold">{product.price?.toFixed(2)}€</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-[#C9A962] text-xs">{t('catalog.view')}</span>
                                  <ExternalLink size={14} className="text-[#C9A962]" />
                                </div>
                              </Link>
                            ))}
                          </div>
                        ) : (
                          <p className="text-[#A1A1AA] text-xs mt-2">
                            {t('catalog.no_expansion_products')}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-[#A1A1AA] bg-[#0A0A0E] rounded-xl border border-white/5">
                        <Layers size={32} className="mx-auto mb-2 opacity-30" />
                        <p className="text-sm">
                          {t('catalog.expansion_not_registered')}
                        </p>
                        <Link 
                          to="/store?category=tcg" 
                          className="inline-block mt-3 px-4 py-2 bg-[#10B981]/20 text-[#10B981] rounded-lg text-sm font-semibold hover:bg-[#10B981]/30"
                        >
                          {t('catalog.view_tcg_store')}
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
